import { formatPhone, formatUserName } from '../../utils/utils';

import { DateUtils, Flex, Icon, Link, Loading, Text } from '@amigoapp/doca-react';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';

const hasUsableDevices = devices => devices.some(device => !!device.label);

const DeviceSection = ({ title, devices, activeDeviceId, isSwitching, action, onSelect }) => (
	<>
		<Flex align="center" gap={2}>
			<Text className="lk-device-panel__section-title">{title}</Text>
			{isSwitching && <Loading className="lk-device-panel__spinner" />}
			{action}
		</Flex>
		{devices.map(device => (
			<button
				key={device.deviceId}
				className={classNames('lk-device-panel__row', { 'lk-device-panel__row--pending': isSwitching })}
				disabled={isSwitching}
				type="button"
				onClick={() => onSelect(device.deviceId)}
			>
				<Icon className={classNames('lk-device-panel__check', { 'lk-device-panel__check--active': device.deviceId === activeDeviceId })} name="check" />
				<span className="lk-device-panel__label">{device.label}</span>
			</button>
		))}
	</>
);

const MetaRow = ({ icon, children }) => (
	<div className="lk-waitingroom__meta-row">
		<Icon className="lk-waitingroom__meta-icon" name={icon} type="regular" />
		{children}
	</div>
);

const PreviewControls = ({ audioEnabled, showDeviceSettings, videoEnabled, onToggleAudio, onToggleDeviceSettings, onToggleVideo }) => (
	<div className="lk-waitingroom__preview-controls">
		<button className={classNames('lk-preview-btn', { 'lk-preview-btn--off': !audioEnabled })} type="button" onClick={onToggleAudio}>
			<Icon name={audioEnabled ? 'microphone' : 'microphone-slash'} type="solid" />
		</button>
		<button className={classNames('lk-preview-btn', { 'lk-preview-btn--off': !videoEnabled })} type="button" onClick={onToggleVideo}>
			<Icon name={videoEnabled ? 'video' : 'video-slash'} type="solid" />
		</button>
		<button className={classNames('lk-preview-btn', { 'lk-preview-btn--active': showDeviceSettings })} type="button" onClick={onToggleDeviceSettings}>
			<Icon name="sliders" type="solid" />
		</button>
	</div>
);

const LiveKitWaitingRoom = ({
	activeCameraId,
	activeMicId,
	activeSpeakerId,
	attendance,
	audioEnabled,
	cameraDevices,
	company,
	createdAt,
	isDoctorReady,
	isSwitchingCamera,
	micDevices,
	previewStatus,
	showDeviceSettings,
	showSpeakerSection,
	speakerDevices,
	user,
	videoEnabled,
	videoTrack,
	onJoinCall,
	onSelectCamera,
	onSelectMic,
	onSelectSpeaker,
	onTestSpeaker,
	onToggleAudio,
	onToggleDeviceSettings,
	onToggleVideo
}) => {
	const videoRef = useRef(null);

	useEffect(() => {
		if (!videoTrack || !videoRef.current) {
			return undefined;
		}

		const element = videoRef.current;

		videoTrack.attach(element);

		return () => videoTrack.detach(element);
	}, [videoTrack]);

	const phone = company.contact_phone || company.contact_phone_2;
	const councilLabel = user.council_name && user.council_number ? `${user.council_name} ${user.council_number}` : '';

	return (
		<Flex align="center" className="lk-waitingroom" direction="col" flex={1} gap={4} justify="between">
			<div className="lk-waitingroom__center">
				<div className="lk-waitingroom__stage">
					<div className="lk-waitingroom__preview-col">
						<div className="lk-waitingroom__preview">
							<video ref={videoRef} autoPlay muted playsInline className="lk-waitingroom__video" />

							{!videoEnabled && (
								<div className="lk-waitingroom__video-off">
									<Icon name="video-slash" type="solid" />
								</div>
							)}

							{videoEnabled && previewStatus !== 'ready' && (
								<div className="lk-waitingroom__video-off">
									{previewStatus === 'pending'
										? <Loading />
										: (
											<Flex align="center" direction="col" gap={2}>
												<Icon name="video-slash" type="solid" />
												<span className="lk-waitingroom__video-message">
													Não foi possível acessar a câmera.
												</span>
											</Flex>
										)}
								</div>
							)}

							<div className="lk-waitingroom__status">
								<span className={classNames('lk-pulse-dot', { 'lk-pulse-dot--ready': isDoctorReady })} />
								<span>{isDoctorReady ? 'O profissional está pronto' : 'Aguardando o profissional'}</span>
							</div>

							<PreviewControls
								audioEnabled={audioEnabled}
								showDeviceSettings={showDeviceSettings}
								videoEnabled={videoEnabled}
								onToggleAudio={onToggleAudio}
								onToggleDeviceSettings={onToggleDeviceSettings}
								onToggleVideo={onToggleVideo}
							/>
						</div>

						{showDeviceSettings && (
							<div className="lk-panel lk-panel--anchored">
								<DeviceSection activeDeviceId={activeCameraId} devices={cameraDevices} isSwitching={isSwitchingCamera} title="Câmera" onSelect={onSelectCamera} />
								{hasUsableDevices(micDevices) && (
									<>
										<div className="lk-panel__divider" />
										<DeviceSection activeDeviceId={activeMicId} devices={micDevices} title="Microfone" onSelect={onSelectMic} />
									</>
								)}
								{showSpeakerSection && hasUsableDevices(speakerDevices) && (
									<>
										<div className="lk-panel__divider" />
										<DeviceSection
											action={<button className="lk-device-panel__test" type="button" onClick={onTestSpeaker}>Testar</button>}
											activeDeviceId={activeSpeakerId}
											devices={speakerDevices}
											title="Saída de áudio"
											onSelect={onSelectSpeaker}
										/>
									</>
								)}
							</div>
						)}
					</div>

					<div className="lk-waitingroom__info-col">
						<div className="lk-waitingroom__info-top">
							<div className="lk-waitingroom__doctor">
								<div className="lk-waitingroom__doctor-avatar">
									<Icon name="user-doctor" type="solid" />
								</div>
								<div className="lk-waitingroom__doctor-text">
									<p className="lk-waitingroom__doctor-name">{formatUserName(user.title, user.name)}</p>
									{councilLabel && <p className="lk-waitingroom__doctor-subtitle">{councilLabel}</p>}
								</div>
							</div>

							<div className="lk-waitingroom__divider" />

							<div className="lk-waitingroom__meta">
								<MetaRow icon="calendar">
									<span className="lk-waitingroom__meta-text">
										{DateUtils.format(attendance?.start_date ? attendance.start_date : createdAt, { format: 'ddd, DD [de] MMMM [-] HH:mm[h]' })}
									</span>
								</MetaRow>
								<MetaRow icon="building">
									<span className="lk-waitingroom__meta-text">{company.name}</span>
								</MetaRow>
								{phone && (
									<MetaRow icon="phone">
										<Link className="lk-waitingroom__meta-link" href={`tel:${phone}`}>{formatPhone(phone)}</Link>
									</MetaRow>
								)}
							</div>
						</div>

						<div className="lk-waitingroom__cta">
							<p className="lk-waitingroom__cta-title">Sua teleconsulta começará em instantes.</p>
							<p className="lk-waitingroom__cta-hint">
								Use fones de ouvido para um ótimo atendimento e eliminar o eco.
							</p>
							<button className="lk-waitingroom__join-btn" disabled={!isDoctorReady} type="button" onClick={onJoinCall}>
								<Icon name="video" type="solid" />
								Entrar na chamada
							</button>
						</div>
					</div>
				</div>
			</div>

			<img alt="Amigo - Logo" className="lk-brand-logo doca-mb-8" src="https://cdn.amigotech.com.br/logos/amigo.svg" />
		</Flex>
	);
};

export default LiveKitWaitingRoom;
